<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('users.title') }}</h1>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('users.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('users.id') }}</th>
                  <th>{{ $t('users.email') }}</th>
                  <th>{{ $t('users.roles') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in users" :key="user.id">
                  <td>{{ user.id }}</td>
                  <td><strong>{{ user.email }}</strong></td>
                  <td>
                    <v-chip
                      v-for="role in user.roles"
                      :key="role.id"
                      size="small"
                      class="mr-1"
                    >
                      {{ role.name }}
                    </v-chip>
                    <span v-if="user.roles.length === 0" class="text-grey">{{ $t('users.noRoles') }}</span>
                  </td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editUserRoles(user)" icon="mdi-account-cog"></v-btn>
                  </td>
                </tr>
                <tr v-if="users.length === 0">
                  <td colspan="4" class="text-center text-grey">{{ $t('users.noUsersFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showRolesDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $t('users.manageRoles', { email: selectedUser?.email }) }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-select
              v-model="selectedRoles"
              :items="allRoles"
              item-title="name"
              item-value="id"
              :label="$t('users.roles')"
              multiple
              chips
              density="compact"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showRolesDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveUserRoles">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiDelete } from '../utils/api'

const { t } = useI18n()

interface Role {
  id: number
  name: string
}

interface User {
  id: number
  email: string
  roles: Role[]
}

const users = ref<User[]>([])
const allRoles = ref<Role[]>([])
const showRolesDialog = ref(false)
const selectedUser = ref<User | null>(null)
const selectedRoles = ref<number[]>([])

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadRoles = async () => {
  try {
    const response = await apiGet('/api/roles')
    if (response.ok) {
      allRoles.value = await response.json()
    }
  } catch (error) {
    console.error('Load roles error:', error)
  }
}

const editUserRoles = (user: User) => {
  selectedUser.value = user
  selectedRoles.value = user.roles.map(r => r.id)
  showRolesDialog.value = true
}

const saveUserRoles = async () => {
  if (!selectedUser.value) return

  try {
    // Get current role IDs
    const currentRoleIds = selectedUser.value.roles.map(r => r.id)
    
    // Remove roles that are no longer selected
    for (const roleId of currentRoleIds) {
      if (!selectedRoles.value.includes(roleId)) {
        await apiDelete(`/api/users/${selectedUser.value.id}/roles/${roleId}`)
      }
    }

    // Add new roles that weren't there before
    for (const roleId of selectedRoles.value) {
      if (!currentRoleIds.includes(roleId)) {
        const response = await apiPost(`/api/users/${selectedUser.value.id}/roles?role_id=${roleId}`)
        if (!response.ok) {
          console.error('Failed to add role:', await response.text())
        }
      }
    }

    await loadUsers()
    showRolesDialog.value = false
  } catch (error) {
    console.error('Save user roles error:', error)
    alert(t('users.failedToUpdateRoles'))
  }
}

onMounted(() => {
  loadUsers()
  loadRoles()
})
</script>

